import { Autocomplete, Badge, Box, Chip, Divider, Drawer, Stack, Switch, TextField, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { RootState } from "src/context/store";
import { useSelector } from "react-redux";
import { ExtraOptions, setExtraOptions } from "src/context/reducers/filter-settings/filterSlice";
import GenericInfiniteScrollList from "src/sections/overview/dashboards/views/generic-infinite-scroll-list";
import { useCallback, useMemo } from "react";

interface Item {
  id: string;
  label: string;
  [key: string]: any;
}

const useSettingsPanel = () => {
  const dispatch = useDispatch();
  const extraOptionsLoaded = useSelector((state: RootState) => state.filters.extraOptionsLoaded);
  const extraOptions = useSelector((state: RootState) => state.filters.extraOptions);

  const handleSwitchActivations = useCallback(() => {
    dispatch(setExtraOptions({
      ...extraOptions,
      mode: extraOptions.mode === "base" ? "activations" : "base",
    }));
  }, [extraOptions, dispatch]);

  const handleSelectionChange = (section: string) => useCallback((selectedItems: any) => {
    const selectedItemsArray = Array.isArray(selectedItems) ? selectedItems : [selectedItems];
    dispatch(setExtraOptions({ ...extraOptions, [section]: selectedItemsArray }));
  }, [extraOptions, dispatch]);



  return {
    handleSwitchActivations,
    extraOptionsLoaded,
    extraOptions,
    handleSelectionChange
  }
}

const useSelection = (section: keyof Omit<ExtraOptions, "mode">) => {
  const extraOptionsLoaded = useSelector((state: RootState) => state.filters.extraOptionsLoaded);
  const extraOptions = useSelector((state: RootState) => state.filters.extraOptions);
  return useMemo(() => {
    return [
      extraOptionsLoaded?.[section]?.map((item) => typeof item === "string" ? ({ id: item, label: item }) : ({ id: item.id, label: item.name })) ?? [],
      extraOptions[section] ?? [] 
    ]
  }, [extraOptionsLoaded, extraOptions]) as [Item[], string[]];
}


export default function SettingsPanel({ open, onClose }: { open: boolean, onClose: () => void }) {
  const {
    extraOptions,
    extraOptionsLoaded,
    handleSelectionChange,
    handleSwitchActivations,
  } = useSettingsPanel();

  const searchFilter = (item: any, searchValue: string) => {
    return item?.label?.toLowerCase().includes(searchValue.toLowerCase());
  }

  const commonProps = useMemo(() => ({
    fullWidth: true,
    searchPlaceholder: "Search",
    showSelectAll: true,
    showOnlyButton: true,
    showItemCount: true,
    itemHeight: 30,
    maxTagWidth: 200
  }), []);

  const selections = {
    campaigns: useSelection("campaigns"),
    ad_groups: useSelection("ad_groups"),
    search_terms: useSelection("search_terms"),
    campaign_types: useSelection("campaign_types"),
    accounts: useSelection("accounts"),
    match_types: useSelection("match_types")
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 345,
          maxWidth: 345,
          overflow: "hidden"
        }
      }}
    >
      <Box width={300} sx={{ p: 3 }}>
        <Typography variant="h6">Additional Filters</Typography>
      </Box>
      <Divider />
      <Box sx={{ position: "relative", m: 2, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }} >
        <Stack direction="row" alignItems="center" spacing={1} px={1} py={0.5}>
          <Typography variant="body1">Activations</Typography>
          <Box flexGrow={1} />
          <Switch checked={extraOptions.mode === "activations"} onChange={handleSwitchActivations} />
        </Stack>
      </Box>
      <Box border="1px solid" borderColor="divider" borderRadius={1} sx={{ m: 2, p: 2, position: "relative"}}>
        <Box sx={{ position: "absolute", top: -15, left: 5, backgroundColor: "background.paper", p: 1, borderRadius: 1 }}>
          <Typography color="text.secondary" fontSize={10} variant="body1">Filters</Typography>
        </Box>
        {/* selections */}
        {Object.entries(selections).map(([key, [items, selectedItems]]) => (
        <Box sx={{ mb: 2 }} key={key}>
          <GenericInfiniteScrollList
            disabled={extraOptions.mode === "base"}
            loading={!extraOptionsLoaded?.[key as keyof ExtraOptions]}
            {...commonProps}
            searchPlaceholder={`${key.charAt(0).toUpperCase() + key.slice(1)}`.replace("_", " ")}
            items={items}
            selectedItems={selectedItems}
            onSelectionChange={handleSelectionChange(key)}
            onOnlyItem={handleSelectionChange(key)}
            customSearchFilter={searchFilter}
          />
          </Box>
        ))}
      </Box>
    </Drawer>
  )
}